/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { environment } from '../../environments/environment';
import { decryptObject, encryptObject } from '../api/Auth';
import { Analytics } from 'analytics';
import segmentPlugin from '@analytics/segment';
import logoImg from '../../assets/logo.png';
import { dsVar } from '../theme/tokens';

export const analytics = Analytics({
  app: 'datascient.ai',
  plugins: [
    segmentPlugin({
      writeKey: 'ld26FUiT4LQvrJrWvHLoPAnmqffzxC35'
    })
  ]
});

const LoginAccount = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const globalParams = new URLSearchParams(window.location.search);
    
    if (params.get('sessionExpired') === 'true' || globalParams.get('sessionExpired') === 'true') {
      handleOpenSnackbar('Your session has expired. Please re-login.');
      
      // Clear from hash search
      if (params.get('sessionExpired') === 'true') {
        navigate('/login', { replace: true });
      }
      
      // Clear from global search if it exists (before the hash)
      if (globalParams.get('sessionExpired') === 'true') {
        const newUrl = window.location.pathname + window.location.hash;
        window.history.replaceState(null, '', newUrl);
      }
    }
  }, [location, navigate]);

  const handleChangeEmail = (event) => {
    setEmail(event.target.value);
  };

  const handleChangePassword = (event) => {
    setPassword(event.target.value);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleOpenSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      handleOpenSnackbar('Email cannot be empty');
      return;
    } else if (!emailRegex.test(email)) {
      handleOpenSnackbar('Invalid email format');
      return;
    } else if (!password) {
      handleOpenSnackbar('Password cannot be empty');
      return;
    } else {
      getRegistration();
    }
  };

  const getRegistration = async () => {
    setLoading(true);
    try {
      let data = { admin_email: email, password: password };
      let encryptedData = encryptObject(data);
      const payload = encryptedData;
      const res = await axios.post(
        `${environment.controlplaneUrl}/auth/signinToAccount`,
        payload
      );
      let decryptedData = decryptObject(res?.data?.data);
      decryptedData = JSON.parse(decryptedData?.data);
      localStorage.setItem('token', decryptedData?.token);
      localStorage.setItem('org_id', decryptedData?.team_id);
      localStorage.setItem("user_email", email);
      setLoading(false);
      // /workspace resolves the default project and forwards into it. replace,
      // so Back from the project page does not land on the login form again.
      navigate("/workspace", { replace: true });
      analytics.identify(`${localStorage.getItem('org_id')}`, {
        firstName: `${localStorage.getItem('org_id')}`,
      });
    } catch (error) {
      console.log('error', error);
      handleOpenSnackbar('Incorrect email or password');
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[100vh] w-full flex-col lg:flex-row overflow-hidden bg-page font-sans text-fg m-0 p-0 text-left">
      {/* Left Column (hidden on mobile) */}
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 relative overflow-hidden border-r border-fg-2/20 bg-page">
        <div className="flex items-center gap-1 z-20">
          <img src={logoImg} alt="Data[S]cient.ai" className="h-8" />
        </div>
        <div className="flex flex-col items-start gap-12 z-10 flex-grow justify-center mt-12">
          <div className="space-y-4 max-w-lg text-left">
            <h1 className="text-5xl font-extrabold leading-tight text-fg tracking-tight font-mono text-left m-0">
              Your <span className="text-accent-fg">data</span>, finally making sense.
            </h1>
            <p className="text-lg text-fg-2 font-normal m-0 text-left">
              From complex patterns to clear priorities in a single click.
            </p>
          </div>
          <div className="w-full max-w-2xl relative">
            <div className="absolute -top-20 -left-20 w-80 h-80 bg-accent/5 rounded-full blur-3xl"></div>
            <div className="relative bg-surface rounded-xl shadow-2xl overflow-hidden border border-fg/10 flex flex-col md:flex-row h-auto min-h-[350px]">
              <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-fg/5 bg-page/50 flex flex-col p-4 gap-4 flex-shrink-0">
                <div className="flex items-center gap-1 mb-2">
                  <img src={logoImg} alt="Data[S]cient.ai" className="h-5" />
                </div>
                <div className="space-y-3">
                  <div className="bg-fg/5 p-3 rounded border border-fg/10 text-left">
                    <p className="text-[10px] text-fg-2 m-0">"Show me the conversion trends for Q3 compared to last year."</p>
                  </div>
                  <div className="bg-accent/5 p-3 rounded border border-accent-fg/20 text-left">
                    <p className="text-[10px] text-accent-fg font-medium italic m-0">Analyzing your dataset...</p>
                    <div className="mt-2 flex gap-1">
                      <div className="w-1 h-1 bg-accent rounded-full"></div>
                      <div className="w-1 h-1 bg-accent rounded-full"></div>
                      <div className="w-1 h-1 bg-accent rounded-full"></div>
                    </div>
                  </div>
                </div>
                <div className="mt-auto relative pt-4 md:pt-0">
                  <div className="w-full h-8 bg-fg/5 rounded border border-fg/10 flex items-center px-2">
                    <div className="w-full h-1 bg-fg/10 rounded"></div>
                    <span className="material-symbols-outlined text-xs text-fg-2 ml-auto">send</span>
                  </div>
                </div>
              </div>
              <div className="flex-1 p-6 flex flex-col overflow-hidden min-w-0">
                <div className="flex justify-between items-center mb-6 flex-shrink-0">
                  <div className="space-y-1">
                    <div className="h-4 w-32 bg-fg/10 rounded"></div>
                    <div className="h-2 w-20 bg-fg/5 rounded"></div>
                  </div>
                  <div className="flex gap-2">
                    <div className="h-8 w-8 rounded bg-fg/5 border border-fg/10"></div>
                    <div className="h-8 w-8 rounded bg-fg/5 border border-fg/10"></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6 flex-shrink-0">
                  <div className="bg-fg/5 p-4 rounded border border-fg/10 flex flex-col justify-between">
                    <div className="flex justify-between mb-4">
                      <div className="w-2 h-2 rounded-full bg-accent"></div>
                      <div className="w-10 h-1 bg-fg/10 rounded"></div>
                    </div>
                    <div className="flex items-end gap-1 h-16 w-full">
                      <div className="flex-1 bg-fg/10 rounded-t h-[40%]"></div>
                      <div className="flex-1 bg-fg/20 rounded-t h-[60%]"></div>
                      <div className="flex-1 bg-accent/40 rounded-t h-[80%]"></div>
                      <div className="flex-1 bg-accent rounded-t h-[100%]"></div>
                      <div className="flex-1 bg-accent/60 rounded-t h-[70%]"></div>
                    </div>
                  </div>
                  <div className="bg-fg/5 p-4 rounded border border-fg/10 flex flex-col justify-between">
                    <div className="flex justify-between mb-4">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                      <div className="w-10 h-1 bg-fg/10 rounded"></div>
                    </div>
                    <div className="flex items-center justify-center h-16 w-full relative">
                      <div className="relative w-10 h-10 rounded-full border-2 border-fg/10 border-t-accent-fg border-r-accent-fg shrink-0"></div>
                    </div>
                  </div>
                </div>
                <div className="bg-fg/5 p-4 rounded border border-fg/10 h-28 relative overflow-hidden flex-shrink-0 w-full">
                  <svg className="absolute bottom-0 left-0 w-full h-[50px]" preserveAspectRatio="none" viewBox="0 0 400 50">
                    <path d="M0 50 V30 Q50 5 100 25 T200 10 T300 35 T400 15 V50 Z" fill="url(#darkGrad)" fillOpacity="0.2"></path>
                    <path d="M0 30 Q50 5 100 25 T200 10 T300 35 T400 15" fill="none" stroke="#FFDE59" strokeWidth="2"></path>
                    <defs>
                      <linearGradient id="darkGrad" x1="0%" x2="0%" y1="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#FFDE59', stopOpacity: 1 }}></stop>
                        <stop offset="100%" style={{ stopColor: '#FFDE59', stopOpacity: 0 }}></stop>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p className="text-sm text-fg-2 z-10 font-mono uppercase tracking-widest text-left mt-8 m-0">
          © 2024 Data[S]cient.ai
        </p>
      </div>

      {/* Right Column (Login Form) */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 md:p-12 lg:p-24 relative bg-page">
        <div className="lg:hidden flex items-center gap-1 mb-12 self-start md:self-center">
          <img src={logoImg} alt="Data[S]cient.ai" className="h-8" />
        </div>

        <div className="w-full max-w-[440px] flex flex-col z-10 mx-auto text-left">
          <div className="mb-10 text-center lg:text-left">
            <h1 className="text-3xl font-extrabold text-fg mb-2 font-mono tracking-tight m-0">Sign-In</h1>
            <p className="text-fg-2 text-lg mt-2 m-0">Access your data intelligence hub.</p>
          </div>

          <div className="flex flex-col gap-3 mb-8 w-full">
            <button className="flex w-full cursor-pointer items-center justify-center gap-3 rounded-lg h-14 bg-surface border border-fg-2/20 hover:bg-fg/5 text-fg text-base font-semibold transition-all shadow-none">
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"></path>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
              </svg>
              Continue with Google
            </button>
            <button className="flex w-full cursor-pointer items-center justify-center gap-3 rounded-lg h-14 bg-surface border border-fg-2/20 hover:bg-fg/5 text-fg text-base font-semibold transition-all shadow-none">
              Continue with SSO
            </button>
          </div>

          <div className="mb-8 text-center w-full">
            <p className="text-fg-2 text-[10px] font-bold uppercase tracking-[0.2em] mb-4 font-mono m-0">Trusted by 500+ enterprises</p>
            <div className="flex justify-center items-center gap-8 opacity-40 grayscale">
              <div className="h-4 w-14 bg-fg-2 rounded-sm"></div>
              <div className="h-4 w-14 bg-fg-2 rounded-sm"></div>
              <div className="h-4 w-14 bg-fg-2 rounded-sm"></div>
            </div>
          </div>

          <div className="relative flex items-center mb-8 w-full">
            <div className="flex-grow border-t border-fg/10"></div>
            <span className="flex-shrink mx-4 text-xs text-fg-2 font-bold uppercase tracking-widest font-mono">or</span>
            <div className="flex-grow border-t border-fg/10"></div>
          </div>

          <form className="space-y-6 w-full m-0" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-fg uppercase tracking-wider m-0" htmlFor="work-email">Email</label>
              <input
                className="flex w-full rounded-lg text-fg bg-fg/5 border border-fg/10 focus:ring-1 focus:ring-accent-fg focus:border-accent-fg focus:outline-none h-14 placeholder:text-fg-2/50 p-4 text-base font-normal transition-all m-0 box-border"
                id="work-email"
                placeholder="name@company.com"
                required
                type="email"
                value={email}
                onChange={handleChangeEmail}
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center w-full">
                <label className="text-xs font-bold text-fg uppercase tracking-wider m-0" htmlFor="password">
                  Password
                </label>
                <span
                  className="text-accent-fg hover:underline cursor-pointer lowercase tracking-normal font-normal text-xs m-0"
                  onClick={() => navigate('/forgotpassword')}
                >
                  Forgot password?
                </span>
              </div>
              <input
                className="flex w-full rounded-lg text-fg bg-fg/5 border border-fg/10 focus:ring-1 focus:ring-accent-fg focus:border-accent-fg focus:outline-none h-14 placeholder:text-fg-2/50 p-4 text-base font-normal transition-all m-0 box-border"
                id="password"
                placeholder="••••••••"
                required
                type="password"
                value={password}
                onChange={handleChangePassword}
              />
            </div>

            <button className="flex w-full cursor-pointer items-center justify-center rounded-lg h-14 bg-accent hover:bg-[#ffe57a] text-on-accent text-base font-extrabold transition-all shadow-lg shadow-accent/5 font-mono uppercase tracking-wider mt-2 border-none" type="submit">
              Login
            </button>
          </form>

          <div className="mt-10 text-center w-full">
            <p className="text-fg-2 text-base m-0">
              New to Data[S]cient.ai?
              <span className="text-accent-fg font-bold hover:underline ml-1 font-mono cursor-pointer" onClick={() => navigate('/signup')}>Create account</span>
            </p>
          </div>
        </div>
      </div>

      {loading && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: dsVar('page', 0.7), display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <CircularProgress style={{ color: dsVar('accent-fg') }} thickness={5} />
        </div>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert elevation={6} variant="filled" onClose={handleCloseSnackbar} severity="error">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default LoginAccount;
